import { useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  FiMail,
  FiSend,
  FiChevronDown,
  FiCheck,
  FiX,
  FiUser,
} from "react-icons/fi";
import { api } from "../../../services/api";
import { Modal, ModalButton } from "../../../components/Modal";

interface ModalSendMailProps {
  showModalSendMail: boolean;
  onHide(showModalSendMail: boolean): void;
  costumer_id: string;
  report_id: string;
}

interface Responsible {
  id: string;
  email: string;
  name: string;
}

export function ModalSendMail({
  showModalSendMail,
  onHide,
  costumer_id,
  report_id,
}: ModalSendMailProps) {
  const [responsibles, setResponsibles] = useState<Responsible[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Carrega responsáveis quando a modal abre
  useEffect(() => {
    if (showModalSendMail && costumer_id) {
      api
        .get("/responsibles", {
          params: {
            costumer_id,
            status: "A",
            limit: 100, // Busca até 100 responsáveis
          },
        })
        .then(response => {
          const responsiblesData = response.data.data || response.data || [];
          // Apenas remove duplicatas por ID
          const uniqueResponsibles = responsiblesData.filter(
            (r: Responsible, index: number, self: Responsible[]) =>
              self.findIndex(item => item.id === r.id) === index,
          );
          setResponsibles(uniqueResponsibles);
        })
        .catch(error => {
          console.error("Erro ao carregar responsáveis:", error);
          setResponsibles([]);
        });
      // Reset form
      setSelectedEmails([]);
      setSubject("");
    }
  }, [showModalSendMail, costumer_id]);

  const hasValidEmail = (email: string | undefined | null): boolean => {
    return !!email && email.trim() !== "";
  };

  const toggleEmail = (email: string) => {
    if (!hasValidEmail(email)) return;
    setSelectedEmails(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email],
    );
  };

  const handleSendEmail = async () => {
    if (selectedEmails.length === 0) {
      enqueueSnackbar("Selecione pelo menos um destinatário", {
        variant: "warning",
      });
      return;
    }

    setSending(true);
    try {
      await api.post("/techVisitReport/email/send", {
        to: selectedEmails.join(", "),
        subject: subject || "Relatório de Visita Técnica",
        body: `Segue o relatório de Visita Técnica https://www.luvi.app/tech-visit-reports/pdf/${report_id}`,
      });
      enqueueSnackbar("E-mail enviado com sucesso!", { variant: "success" });
      onHide(false);
    } catch (error) {
      enqueueSnackbar("Erro ao enviar e-mail", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={showModalSendMail}
      onClose={() => onHide(false)}
      title="Enviar Relatório por E-mail"
      icon={<FiMail size={20} />}
      variant="primary"
      maxWidth="max-w-lg"
      footer={
        <>
          <ModalButton variant="cancel" onClick={() => onHide(false)}>
            Cancelar
          </ModalButton>
          <ModalButton
            variant="primary"
            onClick={handleSendEmail}
            disabled={sending || selectedEmails.length === 0}
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Enviando...
              </>
            ) : (
              <>
                <FiSend size={16} />
                Enviar
              </>
            )}
          </ModalButton>
        </>
      }
    >
      <div className="space-y-4">
        {/* Multi-select de Responsáveis */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">
            Destinatários
          </label>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full min-h-[52px] px-4 py-3 bg-white border border-gray-200 rounded-lg flex items-center justify-between hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
            >
              <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                {selectedEmails.length === 0 ? (
                  <span className="text-gray-400 text-sm">
                    Selecione os destinatários
                  </span>
                ) : (
                  <span className="text-sm text-gray-700">
                    {selectedEmails.length} destinatário
                    {selectedEmails.length > 1 ? "s" : ""} selecionado
                    {selectedEmails.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <FiChevronDown
                className={`text-gray-400 transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Dropdown List */}
            {dropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {selectedEmails.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedEmails([])}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 border-b border-gray-100 flex items-center gap-2"
                  >
                    <FiX size={14} />
                    Limpar seleção
                  </button>
                )}
                {responsibles.length === 0 ? (
                  <div className="px-4 py-6 text-center text-gray-400 text-sm">
                    Nenhum responsável encontrado
                  </div>
                ) : (
                  responsibles.map(responsible => {
                    const emailValid = hasValidEmail(responsible.email);
                    const isSelected = emailValid && selectedEmails.includes(responsible.email);

                    return (
                      <button
                        key={responsible.id}
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (emailValid) {
                            toggleEmail(responsible.email);
                          }
                        }}
                        disabled={!emailValid}
                        className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${
                          !emailValid
                            ? "opacity-50 cursor-not-allowed bg-gray-50"
                            : isSelected
                              ? "bg-blue-50 hover:bg-blue-100"
                              : "hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            !emailValid
                              ? "border-gray-200 bg-gray-100"
                              : isSelected
                                ? "border-[#0085BD] bg-[#0085BD]"
                                : "border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <FiCheck className="text-white" size={12} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-700 flex items-center gap-2">
                            <FiUser size={14} className="text-gray-400" />
                            {responsible.name}
                          </div>
                          <div className={`text-xs truncate ${emailValid ? "text-gray-400" : "text-red-400"}`}>
                            {emailValid ? responsible.email : "Sem e-mail cadastrado"}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
          {/* Emails selecionados como badges */}
          {selectedEmails.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedEmails.map(email => {
                const responsible = responsibles.find(r => r.email === email);
                return (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-[#0085BD]/10 text-[#0085BD] text-xs rounded-full"
                  >
                    {responsible?.name || email}
                    <button
                      type="button"
                      onClick={() => toggleEmail(email)}
                      className="hover:text-red-500 transition-colors"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Campo de Assunto */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">
            Assunto
          </label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Relatório de Visita Técnica"
            className="w-full h-[52px] px-4 rounded-lg text-gray-700 bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
          />
        </div>

        {/* Preview do link */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-1">O e-mail conterá o link:</p>
          <p className="text-sm text-[#0085BD] break-all">
            https://www.luvi.app/tech-visit-reports/pdf/{report_id}
          </p>
        </div>
      </div>
    </Modal>
  );
}
